import React, { useState } from 'react';
import { Plus, Trash2, Upload, Home, MapPin, FileText, DollarSign, Layers } from 'lucide-react';
import { Property, Unit, UnitType, PropertyStatus } from '../types';
import { KUWAIT_LOCATIONS } from '../constants';

interface PropertyFormProps {
  onCancel: () => void;
  onSubmit: (data: Property) => void;
  initialData?: Property | null;
}

const InputGroup = ({ label, icon: Icon, children }: { label: string, icon?: any, children: React.ReactNode }) => (
  <div className="mb-5">
    <label className="block text-sm font-bold text-slate-700 mb-2">{label}</label>
    <div className="relative group">
      {Icon && (
        <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none transition-colors group-focus-within:text-fly-500 z-10">
          <Icon className="h-5 w-5 text-slate-400 group-focus-within:text-fly-500" />
        </div>
      )}
      {children}
    </div>
  </div>
);

const PropertyForm: React.FC<PropertyFormProps> = ({ onCancel, onSubmit, initialData }) => {
  const [formData, setFormData] = useState<Partial<Property>>(initialData ? {
    ...initialData
  } : {
    status: 'نشط',
    units: [],
    type: 'Residential'
  });

  const [units, setUnits] = useState<Partial<Unit>[]>(initialData?.units || []);
  const [selectedGovernorate, setSelectedGovernorate] = useState(initialData?.governorate || '');

  const [showGenerator, setShowGenerator] = useState(false);
  const [genPrefix, setGenPrefix] = useState('');
  const [genStartNumber, setGenStartNumber] = useState(1);
  const [genCount, setGenCount] = useState(5);
  const [genRent, setGenRent] = useState(0);
  const [genType, setGenType] = useState('شقة');

  const unitsEndRef = React.useRef<HTMLTableRowElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'governorate') setSelectedGovernorate(value);
  };

  const addUnit = () => {
    const newUnit: Partial<Unit> = {
      id: Math.random().toString(36).substr(2, 9),
      unitNumber: '',
      type: 'شقة',
      rent: 0,
    };
    setUnits(prev => {
      const next = [...prev, newUnit];
      setTimeout(() => {
        unitsEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
      return next;
    });
  };

  const generateUnits = () => {
    if (genCount <= 0) return;
    const generated: Partial<Unit>[] = [];
    for (let i = 0; i < genCount; i++) {
      generated.push({
        id: Math.random().toString(36).substr(2, 9),
        unitNumber: `${genPrefix}${genStartNumber + i}`,
        type: genType,
        rent: genRent,
        contractRent: genRent
      });
    }
    setUnits(prev => {
      const next = [...prev, ...generated];
      setTimeout(() => {
        unitsEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
      return next;
    });
    setShowGenerator(false);
  };

  const updateUnit = (index: number, field: keyof Unit, value: any) => {
    const newUnits = [...units];
    newUnits[index] = { ...newUnits[index], [field]: value };
    setUnits(newUnits);
  };

  const removeUnit = (index: number) => {
    const newUnits = [...units];
    newUnits.splice(index, 1);
    setUnits(newUnits);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Remove temporary string IDs from new units to force backend INSERT instead of UPDATE
    const cleanedUnits = units.map(u => {
      const cleaned = { ...u };
      const unitIdStr = String(cleaned.id || '');
      // If it's a generated random string (contains letters or is NaN), delete the id
      if (unitIdStr && isNaN(Number(unitIdStr))) {
        delete cleaned.id;
        delete (cleaned as any).u_id; // just in case
      }
      return cleaned;
    });

    const finalData = { 
      ...formData, 
      id: initialData?.id || Math.random().toString(), 
      units: cleanedUnits as Unit[] 
    } as Property;
    onSubmit(finalData);
  };

  // UPDATED COLORS: Blue/Gray standard
  const inputClass = "block w-full pr-11 bg-white border border-slate-300 rounded-xl py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all shadow-sm";
  const selectClass = "block w-full pr-11 bg-white border border-slate-300 rounded-xl py-3 text-slate-900 focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all shadow-sm appearance-none";

  return (
    <form onSubmit={handleSubmit} className="bg-slate-50 rounded-2xl shadow-xl border border-slate-200 overflow-hidden flex flex-col max-h-[96vh]">
      <div className="bg-white p-5 border-b border-slate-200 flex justify-between items-center shrink-0">
        <div>
          <h2 className="text-xl font-bold text-slate-800">
            {initialData ? 'تعديل بيانات العقار' : 'إضافة عقار جديد'}
          </h2>
          <p className="text-slate-500 text-[10px] mt-1">
            {initialData ? 'تحديث معلومات العقار والوحدات التابعة له' : 'يرجى ملء كافة التفاصيل المطلوبة'}
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-5 sm:p-8 space-y-6 custom-scrollbar">
        {/* Basic Info */}
        <section className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
            <div className="bg-fly-50 p-2 rounded-lg">
                <Home className="text-fly-600 w-5 h-5" />
            </div>
            <h3 className="text-lg font-bold text-slate-800">بيانات العقار الأساسية</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-2">
            <div className="md:col-span-12 grid grid-cols-1 md:grid-cols-2 gap-5">
              <InputGroup label="إسم العقار *" icon={Home}>
                <input name="name" value={formData.name || ''} onChange={handleInputChange} required className={inputClass} placeholder="مثال: برج التحرير" />
              </InputGroup>
              <InputGroup label="إسم المالك" icon={FileText}>
                <input name="ownerName" value={formData.ownerName || ''} onChange={handleInputChange} className={inputClass} placeholder="إترك فارغاً للإسم الافتراضي" />
              </InputGroup>
            </div>
            
            <div className="md:col-span-12">
              <InputGroup label="حالة العقار *" icon={Layers}>
                <select name="status" value={formData.status || 'نشط'} onChange={handleInputChange} className={selectClass}>
                  <option value="نشط">نشط</option>
                  <option value="تحت الصيانة">تحت الصيانة</option>
                  <option value="مباع">مباع</option>
                </select>
              </InputGroup>
            </div>

            <div className="md:col-span-12 grid grid-cols-1 md:grid-cols-3 gap-5">
               <InputGroup label="المحافظة" icon={MapPin}>
                  <select name="governorate" value={formData.governorate || ''} onChange={handleInputChange} className={selectClass}>
                    <option value="">إختر المحافظة</option>
                    {KUWAIT_LOCATIONS && Object.keys(KUWAIT_LOCATIONS).map(gov => (
                        <option key={gov} value={gov}>{gov}</option>
                    ))}
                  </select>
               </InputGroup>
               
               <InputGroup label="المدينة / المنطقة" icon={MapPin}>
                  <select name="city" value={formData.city || ''} onChange={handleInputChange} className={selectClass} disabled={!selectedGovernorate}>
                    <option value="">إختر المنطقة</option>
                     {selectedGovernorate && (KUWAIT_LOCATIONS as any)[selectedGovernorate]?.map((city: string) => (
                        <option key={city} value={city}>{city}</option>
                     ))}
                  </select>
               </InputGroup>

               <InputGroup label="قطعة" icon={MapPin}>
                  <input name="block" value={formData.block || ''} onChange={handleInputChange} className={inputClass} placeholder="12" />
               </InputGroup>
            </div>

            <div className="md:col-span-12 grid grid-cols-1 md:grid-cols-3 gap-5">
               <InputGroup label="نوع العقار" icon={Layers}>
                  <select name="type" value={formData.type || 'Residential'} onChange={handleInputChange} className={selectClass}>
                    <option value="Residential">سكني</option>
                    <option value="Commercial">تجاري</option>
                    <option value="Investment">إستثماري</option>
                  </select>
               </InputGroup>
               <InputGroup label="رقم المبنى" icon={Home}>
                 <input name="buildingNumber" value={formData.buildingNumber || ''} onChange={handleInputChange} className={inputClass} />
               </InputGroup>
               <InputGroup label="الرقم المدني" icon={FileText}>
                 <input name="civilId" value={formData.civilId || ''} onChange={handleInputChange} className={inputClass} />
               </InputGroup>
            </div>
          </div>
        </section>


        {/* Units */}
        <section className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-100 shadow-sm">
          <div className="flex justify-between items-center mb-6 pb-4 border-b border-slate-100">
             <div className="flex items-center gap-3">
                  <div className="bg-blue-50 p-2 rounded-lg">
                     <Layers className="text-blue-600 w-5 h-5" />
                  </div>
                 <h3 className="text-lg font-bold text-slate-800">وحدات العقار</h3>
             </div>
             <div className="flex gap-2">
                 <button type="button" onClick={() => setShowGenerator(!showGenerator)} className="px-4 py-2 bg-slate-800 text-white rounded-lg text-xs font-bold hover:bg-slate-900 flex items-center gap-2 shadow-lg transition-all">
                    <Layers size={14} />
                    <span>توليد تلقائي</span>
                </button>
                 <button type="button" onClick={addUnit} className="px-4 py-2 bg-fly-600 text-white rounded-lg text-xs font-bold hover:bg-fly-700 flex items-center gap-2 shadow-lg shadow-fly-600/20 transition-all">
                    <Plus size={14} />
                    <span>إضافة وحدة</span>
                </button>
             </div>
          </div>

          {showGenerator && (
            <div className="mb-6 p-5 bg-slate-50 border border-slate-200 rounded-xl space-y-4">
              <h4 className="text-sm font-bold text-slate-800 border-b border-slate-200 pb-2">توليد الوحدات تلقائياً</h4>
              <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 mb-1">الحرف / البادئة</label>
                  <input type="text" value={genPrefix} onChange={e => setGenPrefix(e.target.value)} className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs" placeholder="مثال: A أو شقة " />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 mb-1">رقم البداية</label>
                  <input type="number" value={genStartNumber} onChange={e => setGenStartNumber(Number(e.target.value))} className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 mb-1">العدد</label>
                  <input type="number" min="1" value={genCount} onChange={e => setGenCount(Number(e.target.value))} className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 mb-1">الإيجار الافتراضي</label>
                  <input type="number" min="0" step="0.001" value={genRent} onChange={e => setGenRent(Number(e.target.value))} className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 mb-1">النوع</label>
                  <select value={genType} onChange={e => setGenType(e.target.value)} className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs bg-white">
                    {Object.values(UnitType).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowGenerator(false)} className="px-3 py-1.5 bg-white border border-slate-300 text-slate-600 rounded-lg text-xs font-bold">إلغاء</button>
                <button type="button" onClick={generateUnits} className="px-4 py-1.5 bg-fly-600 text-white rounded-lg text-xs font-bold">توليد الآن</button>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-xs text-right">
                <thead className="bg-slate-50 text-slate-700 font-bold border-b border-slate-200">
                    <tr>
                        <th className="p-3">رقم الوحدة *</th>
                        <th className="p-3">النوع</th>
                        <th className="p-3">الإيجار الفعلي *</th>
                        <th className="p-3">القيمة الدفترية (للإيصالات)</th>
                        <th className="p-3 w-10"></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                    {units.map((unit, index) => (
                        <tr key={index} className="hover:bg-slate-50 transition-colors group">
                            <td className="p-2">
                                <input 
                                    value={unit.unitNumber} 
                                    onChange={(e) => updateUnit(index, 'unitNumber', e.target.value)}
                                    className="w-full border border-slate-200 rounded-lg p-2 focus:border-fly-500 outline-none transition-all text-sm" 
                                    placeholder="A1"
                                />
                            </td>
                            <td className="p-2">
                                <select 
                                    value={unit.type}
                                    onChange={(e) => updateUnit(index, 'type', e.target.value)}
                                    className="w-full border border-slate-200 rounded-lg p-2 focus:border-fly-500 outline-none transition-all text-sm bg-white"
                                >
                                    {Object.values(UnitType).map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </td>
                            <td className="p-2">
                                <input 
                                    type="number"
                                    min="0"
                                    step="0.001"
                                    value={unit.rent || ''} 
                                    onChange={(e) => updateUnit(index, 'rent', e.target.value ? Number(e.target.value) : 0)}
                                    className="w-full border border-slate-200 rounded-lg p-2 focus:border-fly-500 outline-none transition-all text-sm" 
                                />
                            </td>
                            <td className="p-2">
                                <input 
                                    type="number"
                                    min="0"
                                    step="0.001"
                                    value={unit.contractRent !== undefined ? unit.contractRent : (unit.rent || '')} 
                                    onChange={(e) => updateUnit(index, 'contractRent', e.target.value ? Number(e.target.value) : 0)}
                                    className="w-full border border-slate-200 rounded-lg p-2 focus:border-fly-500 outline-none transition-all text-sm" 
                                />
                            </td>
                            <td className="p-2 text-center">
                                <button type="button" onClick={() => removeUnit(index)} className="text-slate-300 hover:text-red-500 p-1.5 transition-all">
                                    <Trash2 size={16} />
                                </button>
                            </td>
                        </tr>
                    ))}
                    <tr ref={unitsEndRef} style={{ height: 0 }} />
                </tbody>
            </table>
          </div>
        </section>
      </div>

      <div className="p-5 bg-white border-t border-slate-200 flex justify-center gap-4 shrink-0">
        <button type="button" onClick={onCancel} className="flex-1 sm:flex-none px-8 py-3 bg-white border border-slate-300 text-slate-700 font-bold rounded-xl hover:bg-slate-50 transition-all text-sm">إلغاء</button>
        <button type="submit" className="flex-1 sm:flex-none px-12 py-3 bg-fly-600 text-white font-bold rounded-xl hover:bg-fly-700 shadow-lg shadow-fly-600/20 transition-all text-sm">حفظ البيانات</button>
      </div>
    </form>
  );
};

export default PropertyForm;